'use client';

import { usePathname } from 'next/navigation';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { fetchCoins } from '@/lib/api/transaction/client';
import type { Coin } from '@/lib/api/transaction/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { readCachedCoins, writeCachedCoins } from '@/lib/utils/coinsSnapshot';
import { getZoneFromPath } from '@/lib/utils/zones';

interface CoinsContextValue {
  coins: Coin[];
  coinsById: Record<string, Coin>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const CoinsContext = createContext<CoinsContextValue | undefined>(undefined);

export function CoinsProvider({ children }: { children: ReactNode }) {
  const [coins, setCoins] = useState<Coin[]>(readCachedCoins);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { isAuthenticated } = useAuthContext();

  const isTradeZone = getZoneFromPath(usePathname()) === 'trade';

  const coinsById = coins.reduce<Record<string, Coin>>((acc, coin) => {
    acc[coin.id] = coin;
    return acc;
  }, {});

  const loadCoins = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedCoins = await fetchCoins();
      setCoins(fetchedCoins);
      writeCachedCoins(fetchedCoins);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch coins');
      setError(error);
      console.error('CoinsProvider: Failed to fetch coins:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const shouldLoad = isTradeZone || isAuthenticated;

  useEffect(() => {
    if (!shouldLoad) {
      setIsLoading(false);
      return;
    }
    loadCoins();
  }, [shouldLoad, loadCoins]);

  return (
    <CoinsContext.Provider value={{ coins, coinsById, isLoading, error, refetch: loadCoins }}>
      {children}
    </CoinsContext.Provider>
  );
}

export function useCoins() {
  const context = useContext(CoinsContext);
  if (!context) {
    throw new Error('useCoins must be used within CoinsProvider');
  }
  return context;
}
